import {useState} from "react";
import PostStockButton from "../../components/PostStockButton";
import EditorSection from "../../components/EditorSection";
import EntityLabel from "../../components/EntityLabel";
import { lookupProduct } from "../../components/InventoryLines/ProductUnitPicker";
import { Button, Dropdown, Input } from "@akin2unde/flowui-react";
import ManagementPageHTML, { IdentityCell, StatusBadge, } from "../../components/ManagementPage/ManagementPageHTML";
import type { InventoryTransfer, InventoryTransferItem, } from "../../models/InventoryTransfer";
import { InventoryTransferService } from "../../services/InventoryTransferService";
import { useWarehouse } from "../../warehouse/WarehouseContext";
import { useInventoryLookups } from "../../hooks/useInventoryLookups";
import { Status } from "../../models/Status";
import { useAuth } from "../../auth/useAuth";
import "../../components/InventoryLines/InventoryLines.css";
export default function InventoryTransferHTML() {
    const [revision,setRevision]=useState(0);
  const { Warehouse } = useWarehouse();
    const { products, warehouses, loading, error } = useInventoryLookups();
    const { hasPermission } = useAuth();
    const manage = hasPermission("InventoryTransfer.Manage") ||
        hasPermission("Inventory.Manage");
    const name = (code: string) => String(warehouses.find((x) => x.Key === code)?.Value ?? code);
    return (<>
      <p role={error ? "alert" : "status"}>
        {error || (loading ? "Loading transfer options…" : "")}
      </p>
      <ManagementPageHTML<InventoryTransfer> key={`${Warehouse?.Code}:${revision}`} RenderRowActions={row=>!row.IsCompleted && hasPermission("InventoryTransfer.Post") && row.WareHouse===Warehouse?.Code ? <PostStockButton kind="transfer" code={row.Code} onPosted={()=>setRevision(v=>v+1)}/>:null} Title="Stock transfers" Eyebrow="Between warehouses" Description="Record outgoing requests and review transfers involving this warehouse. Enter quantities in base stock units. Save the transfer, then use Post to stock to move approved quantities. Posted transfers are locked." AddLabel="Create transfer" SearchPlaceholder="Search transfer code or comment" Rows={[]} Summaries={[]} Service={InventoryTransferService(Warehouse?.Code)} CanExport={false} CanCreate={manage && !loading && !error} CanEdit={manage && !loading && !error} CanDelete={manage} CanEditRow={(x) => x.WareHouse === Warehouse?.Code && !x.IsCompleted} CanDeleteRow={(x) => x.WareHouse === Warehouse?.Code && !x.IsCompleted} DefaultValues={{
            WareHouse: Warehouse?.Code,
            Items: [],
            Status: Status.Active,
            IsCompleted: false,
        }} GetRowKey={(x) => x.Code} GetSearchText={(x) => `${x.Code} ${x.WareHouse} ${x.ToWareHouse} ${x.Comment ?? ""}`} GetStatus={(x) => x.Status} Statuses={Object.values(Status)} Columns={[
            {
                Key: "Code",
                Header: "Transfer",
                Render: (x) => (<IdentityCell Icon="fa-right-left" Title={x.Code} Subtitle={x.WareHouse === Warehouse?.Code ? "Outgoing" : "Incoming"}/>),
            },
            { Key: "From", Header: "From", Render: (x) => <EntityLabel entity="warehouse" code={x.WareHouse}/> },
            { Key: "To", Header: "To", Render: (x) => <EntityLabel entity="warehouse" code={x.ToWareHouse}/> },
            {
                Key: "Items",
                Header: "Items",
                Render: (x) => (<details>
                <summary>{x.Items?.length ?? 0} products</summary>
                <ul>
                  {(x.Items ?? []).map((l, i) => (<li key={i}>
                      {String(products.find((p) => p.Key === l.ProductCode)?.Value ??
                            l.ProductCode)}{" "}
                      · requested {l.QuantityRequest} · approved{" "}
                      {l.QuantityApproved} · {l.Status} · {l.Reason ?? ""}
                    </li>))}
                </ul>
              </details>),
            },
            {
                Key: "Complete",
                Header: "Record",
                Render: (x) => (x.IsCompleted ? "Posted · locked" : "Draft"),
            },
            {
                Key: "Status",
                Header: "Status",
                Render: (x) => <StatusBadge Status={x.Status}/>,
            },
        ]} DialogFields={[
            { Key: "Code", Label: "Transfer code", Disable: true },
            {
                Key: "WareHouse",
                Label: "From warehouse",
                Type: "select",
                Disable: true,
                Required: true,
                Options: [
                    {
                        display: Warehouse?.Name ?? "",
                        value: Warehouse?.Code ?? "",
                        other: null,
                    },
                ],
            },
            {
                Key: "ToWareHouse",
                Label: "To warehouse",
                Type: "select",
                Required: true,
                Options: warehouses
                    .filter((x) => x.Key !== Warehouse?.Code)
                    .map((x) => ({
                    display: String(x.Value),
                    value: x.Key,
                    other: null,
                })),
            },
            {
                Key: "Comment",
                Label: "Comment",
                Type: "textarea",
                FullWidth: true,
            },
            {
                Key: "Status",
                Label: "Status",
                Type: "select",
                Required: true,
                Options: Object.values(Status),
            },

        ]} ValidateForm={(v): Record<string, string> => {
            const errors: Record<string, string> = {};
            if (v.WareHouse !== Warehouse?.Code)
                errors.WareHouse =
                    "Transfers must originate from your operating warehouse.";
            if (v.ToWareHouse === v.WareHouse)
                errors.ToWareHouse = "Choose a different destination warehouse.";
            const lines = v.Items as InventoryTransferItem[];
            if (!lines?.length)
                errors.Items = "Add at least one product.";
            const codes = new Set<string>();
            for (const x of lines ?? []) {
                if (!x.ProductCode ||
                    codes.has(x.ProductCode) ||
                    !Number.isFinite(x.QuantityRequest) ||
                    x.QuantityRequest <= 0 ||
                    !Number.isFinite(x.QuantityApproved) ||
                    x.QuantityApproved < 0 ||
                    x.QuantityApproved > x.QuantityRequest)
                    errors.Items =
                        "Select unique products, request positive quantities, and approve between zero and the requested quantity.";
                codes.add(x.ProductCode);
            }
            return errors;
        }} RenderDialogContent={({ FormValues, SetFormValue, ValidationErrors, }) => {
            const items = (FormValues.Items ?? []) as InventoryTransferItem[];
            const update = (i: number, patch: Partial<InventoryTransferItem>) => SetFormValue("Items", items.map((x, n) => (n === i ? { ...x, ...patch } : x)));
            return (<div className="inventory-lines">
              <h3>Transfer items</h3>
              {items.map((x, i) => (<EditorSection key={i} className="admin-section line-section" title={<>Item {i + 1}</>}><div className="line-fields">
                  <p>Base unit: {lookupProduct(products, x.ProductCode)?.BaseUnitCode || lookupProduct(products, x.ProductCode)?.UnitPrices?.find(u => u.BaseQuantity === 1)?.UnitCode || "Not configured"}</p>
                  <label>
                    Product
                    <Dropdown value={x.ProductCode} options={products.map((p) => ({
                        display: String(p.Value),
                        value: p.Key,
                        other: null,
                    }))} onChange={(p) => update(i, { ProductCode: String(p.value) })}/>
                  </label>
                  <label>
                    Requested quantity (base units)
                    <Input type="number" min={0} step={0.01} value={x.QuantityRequest} onChange={(e) => update(i, { QuantityRequest: Number(e.target.value) })}/>
                  </label>
                  <label>
                    Approved quantity (base units)
                    <Input type="number" min={0} step={0.01} value={x.QuantityApproved} onChange={(e) => update(i, { QuantityApproved: Number(e.target.value) })}/>
                  </label>
                  <label>
                    Reason
                    <Input value={x.Reason ?? ""} onChange={(e) => update(i, { Reason: e.target.value })}/>
                  </label>
                  <label>
                    Status
                    <Dropdown value={x.Status} options={Object.values(Status).map((v) => ({
                        display: v,
                        value: v,
                        other: null,
                    }))} onChange={(p) => update(i, { Status: String(p.value) as Status })}/>
                  </label>
                  <Button variant="outline" onClick={() => SetFormValue("Items", items.filter((_, n) => n !== i))}>
                    Remove item {i + 1}
                  </Button>
                </div></EditorSection>))}
              <Button variant="outline" onClick={() => SetFormValue("Items", [
                    ...items,
                    {
                        ProductCode: "",
                        QuantityRequest: 1,
                        QuantityApproved: 0,
                        Status: Status.Active,
                        Reason: "",
                    },
                ])}>
                Add product
              </Button>
              {ValidationErrors.Items && (<p role="alert">{ValidationErrors.Items}</p>)}
            </div>);
        }}/>
    </>);
}
